/**
 * ThreadCategoryList — the canonical grouped thread renderer (Round 7).
 *
 * Groups threads by Type into category branch nodes (ordered by
 * THREAD_TYPE_ORDER), each a standard outline section row: collapsible,
 * zoomable via its bullet, count meta, and an ActionMenu with
 * Expand all / Collapse all. Children are canonical ThreadNodes.
 *
 * Used by:
 *   - ThreadsSection (Reference > Threads; scopeKey 'thcat:all';
 *     category zoom key sec:threads:cat:<Type>)
 *   - ScheduleSection PhaseThreadsContent (Phase > Threads;
 *     scopeKey 'thcat:<phId>'; zoom key sec:phase:<phId>:thcat:<Type>)
 *   - MeetingsSection MeetingThreadsContent (a meeting's minutes;
 *     scopeKey 'mtgcat:<meetingId>'; meetingId passed through to
 *     ThreadNode so it can offer "Remove from meeting")
 *
 * Props:
 *   threads        — thread records to group (already scope-filtered)
 *   projectId      — for Slack panel context
 *   projectName    — for Slack panel title
 *   scopeKey       — collapse-key prefix; category key = scopeKey + ':' + type
 *   onZoomCategory — (type) => void; bullet click navigation
 *   meetingId      — optional; when set, passed through to each ThreadNode
 */

import { wf, saveViewState } from '../outline/state.js'
import { groupThreadsByType, threadCounts } from '../services/threads.js'
import ThreadNode from './ThreadNode.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'

/**
 * ThreadCountsMeta — open/closed tallies for a thread list. Shown on Threads branch nodes (phase +
 * Reference root) and on every category node. The red overdue badge went with thread target dates
 * (2026-09-10) — a thread is late only through the plane it rides.
 */
export function ThreadCountsMeta({ threads }) {
  if (!Array.isArray(threads) || threads.length === 0) return null
  const { open, closed } = threadCounts(threads)
  return (
    <span className="wf-prog" style={{ marginLeft: 8 }}>
      <span style={{ color: open > 0 ? '#1D9E75' : '#7a9aaa' }}>{open} Open</span>
      <span style={{ color: '#7a9aaa' }}> · {closed} Closed</span>
    </span>
  )
}

function CategoryNode({ type, threads, projectId, projectName, scopeKey, onZoomCategory, meetingId }) {
  const key = scopeKey + ':' + type
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()

  function toggle(e) {
    e.stopPropagation()
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function setAll(expand) {
    wf.collapsed[key] = !expand
    threads.forEach(t => { wf.collapsed['thread:' + t.id] = !expand })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          title={'Focus on ' + type}
          {...(onZoomCategory ? { 'data-kbzoom': '1' } : {})}
          onClick={e => { e.stopPropagation(); onZoomCategory && onZoomCategory(type) }}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{type}</span>
          <ThreadCountsMeta threads={threads} />
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setAll(true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {threads.map(t => (
            <ThreadNode key={t.id} item={t} projectId={projectId} projectName={projectName} meetingId={meetingId} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function ThreadCategoryList({ threads, projectId, projectName, scopeKey, onZoomCategory, meetingId }) {
  const groups = groupThreadsByType(threads)
  return groups.map(g => (
    <CategoryNode
      key={g.type}
      type={g.type}
      threads={g.threads}
      projectId={projectId}
      projectName={projectName}
      scopeKey={scopeKey}
      onZoomCategory={onZoomCategory}
      meetingId={meetingId}
    />
  ))
}
